import { useCallback, useEffect, useState, type RefObject } from "react";

function getFullscreenElement(): Element | null {
  const doc = document as Document & { webkitFullscreenElement?: Element | null };
  return document.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

function nativeFullscreenAvailable(): boolean {
  const doc = document as Document & {
    webkitFullscreenEnabled?: boolean;
    mozFullScreenEnabled?: boolean;
    msFullscreenEnabled?: boolean;
  };
  return Boolean(
    doc.fullscreenEnabled ||
      doc.webkitFullscreenEnabled ||
      doc.mozFullScreenEnabled ||
      doc.msFullscreenEnabled,
  );
}

async function requestNativeFullscreen(el: HTMLElement): Promise<boolean> {
  const anyEl = el as HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void> | void;
    webkitRequestFullScreen?: () => Promise<void> | void;
  };
  try {
    if (typeof el.requestFullscreen === "function") {
      await el.requestFullscreen();
      return true;
    }
    if (typeof anyEl.webkitRequestFullscreen === "function") {
      await anyEl.webkitRequestFullscreen();
      return true;
    }
    if (typeof anyEl.webkitRequestFullScreen === "function") {
      await anyEl.webkitRequestFullScreen();
      return true;
    }
  } catch {
    return false;
  }
  return false;
}

async function exitNativeFullscreen(): Promise<void> {
  const doc = document as Document & { webkitExitFullscreen?: () => Promise<void> | void };
  try {
    if (typeof document.exitFullscreen === "function" && getFullscreenElement()) {
      await document.exitFullscreen();
    } else if (typeof doc.webkitExitFullscreen === "function" && getFullscreenElement()) {
      await doc.webkitExitFullscreen();
    }
  } catch {
    // O fallback CSS cobre o restante.
  }
}

/**
 * Tela cheia da videochamada. Usa a API nativa quando o navegador permite;
 * no iPad/Chrome iOS (e outros sem Fullscreen API) amplia o container com CSS.
 */
export function useMeetingFullscreen(targetRef: RefObject<HTMLElement | null>) {
  const [fakeFullscreen, setFakeFullscreen] = useState(false);
  const [nativeFullscreen, setNativeFullscreen] = useState(false);

  const isFullscreen = fakeFullscreen || nativeFullscreen;

  useEffect(() => {
    const syncNative = () => {
      const el = targetRef.current;
      setNativeFullscreen(Boolean(el && getFullscreenElement() === el));
    };
    syncNative();
    document.addEventListener("fullscreenchange", syncNative);
    document.addEventListener("webkitfullscreenchange", syncNative);
    return () => {
      document.removeEventListener("fullscreenchange", syncNative);
      document.removeEventListener("webkitfullscreenchange", syncNative);
    };
  }, [targetRef]);

  useEffect(() => {
    if (!fakeFullscreen) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setFakeFullscreen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [fakeFullscreen]);

  const enter = useCallback(async () => {
    const el = targetRef.current;
    if (!el) {
      return;
    }
    if (nativeFullscreenAvailable()) {
      const ok = await requestNativeFullscreen(el);
      if (ok) {
        setFakeFullscreen(false);
        return;
      }
    }
    setFakeFullscreen(true);
  }, [targetRef]);

  const exit = useCallback(async () => {
    setFakeFullscreen(false);
    await exitNativeFullscreen();
  }, []);

  const toggle = useCallback(async () => {
    if (isFullscreen) {
      await exit();
      return;
    }
    await enter();
  }, [enter, exit, isFullscreen]);

  return { isFullscreen, fakeFullscreen, toggle, exit };
}
